import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { useDestination, API } from "../App";
import { normalizeCatalogProduct } from "../lib/catalogProduct";
import { fetchLiveProduct } from "../services/liveCatalogApi";
import LuxuryProductImage from "../components/LuxuryProductImage";
import { applyImportSeoMeta, toImportSlug } from "../lib/importSeo";

/**
 * Indexable import concierge route: /imports/:category/:item
 * Full product data rendered server-crawlable in the DOM (no modals/tabs).
 */
export default function ImportItemPage() {
  const { category, item } = useParams();
  const { destination, currency } = useDestination();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await fetchLiveProduct(API, item, { destination, currency });
        if (!active) return;
        const normalized = normalizeCatalogProduct(data, { destination, currency });
        setProduct(normalized);

        const canonicalUrl = `${window.location.origin}/imports/${toImportSlug(category)}/${toImportSlug(item)}`;
        applyImportSeoMeta({
          title: `${normalized.name} | CheepSweepz ${category} Import`,
          description: `${normalized.name} — guaranteed delivered import pricing with profit cost included. Category: ${category}.`,
          price: normalized.landed_price ?? normalized.price ?? normalized.list_price,
          currency: normalized.display_currency || currency || "USD",
          canonicalUrl,
          image: normalized.images?.[0] || normalized.real_images?.[0],
        });
      } catch (loadError) {
        if (active) {
          setProduct(null);
          setError(loadError?.message || "Import listing unavailable");
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [category, item, destination, currency]);

  const price = Number(product?.landed_price || product?.price || 0).toFixed(2);
  const displayCurrency = product?.display_currency || currency || "USD";

  return (
    <Layout>
      <article className="container mx-auto px-4 py-8 max-w-5xl">
        <Link to="/products" className="text-orange-400 text-sm mb-4 inline-block">
          ← All import products
        </Link>

        {loading ? (
          <p className="text-slate-500">Loading import listing…</p>
        ) : error ? (
          <p className="text-red-400">{error}</p>
        ) : product ? (
          <>
            <header className="mb-8">
              <p className="text-sm uppercase tracking-wider text-orange-400 mb-2">{category} import</p>
              <h1 className="text-3xl font-bold text-white mb-3">{product.name}</h1>
              <p className="text-slate-300 max-w-3xl">
                Guaranteed delivered price including profit cost add-on. Marketplace: {product.marketplace}.
              </p>
              <p className="text-2xl font-semibold text-emerald-400 mt-4">
                {displayCurrency} {price}
              </p>
            </header>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <LuxuryProductImage
                product={product}
                className="rounded-xl overflow-hidden border border-slate-800"
              />
              <section>
                <h2 className="text-xl font-semibold text-white mb-3">Import concierge details</h2>
                <p className="text-slate-300 mb-4">
                  {product.description || `${product.name} imported to the Caribbean with CheepSweepz logistics.`}
                </p>
                <ul className="text-slate-400 space-y-2 text-sm">
                  <li>Category: {category}</li>
                  <li>Product ID: {product.product_id || item}</li>
                  <li>Destination: {destination}</li>
                  <li>Pricing engine: DHC HQ profit-cost sync (17.5% VAT)</li>
                  <li>Marketplace: {product.marketplace}</li>
                </ul>
                <Link
                  to={`/products/${product.product_id || item}`}
                  className="inline-flex mt-6 px-4 py-2 rounded-lg bg-orange-500 text-white font-medium"
                >
                  Add to cart
                </Link>
              </section>
            </div>
          </>
        ) : null}
      </article>
    </Layout>
  );
}
